import React, { useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import JSZip from 'jszip';

import type { TLocalPackage, UploadMode } from '../../../hoc/Layout/Sidebar/SiderbarModals/UploadContentModal';
import UploadFileIcon from '../../../assets/icons/upload-icon-file.svg';
import { findSearchValue, getVideoResolution, inspectVideoFileForUpload, isChrome, isSafari } from '../../../helpers';
import LinearProgress from '../LinearProgress';
import { NOTIFICATION } from '../../../enums';
import Modal from '../Modal';
import { ContainedButton, OutlinedButton } from '../Buttons';
import styles from './FileUpload.module.scss';

type TFileUpload = {
  setPackageList: (arr: TLocalPackage[]) => void;
  small?: boolean;
  disable?: boolean;
  setRearrangeDisabled: (disabled: boolean) => void;
};

type UploadPrecheckIssue = {
  targetPath: string;
  severity: 'blocking' | 'warning';
  message: string;
};

type UploadPrecheckGroup = {
  targetPath: string;
  issues: UploadPrecheckIssue[];
  blockingCount: number;
  warningCount: number;
};

const FileUpload: React.FC<TFileUpload> = ({ setPackageList, small, disable = false, setRearrangeDisabled }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isIOSDevice =
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isChromeBrowser = isChrome();
  const isSafariBrowser = isSafari();
  const filePickerProps = isIOSDevice || isChromeBrowser
    ? { accept: '.mp4,.mov,.m4v,video/mp4,video/quicktime' }
    : { webkitdirectory: 'true', directory: 'true' };
  const notification = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [showUploadPrecheckModal, setShowUploadPrecheckModal] = useState(false);
  const [uploadPrecheckGroups, setUploadPrecheckGroups] = useState<UploadPrecheckGroup[]>([]);
  const [expandedPrecheckPaths, setExpandedPrecheckPaths] = useState<Record<string, boolean>>({});
  const [pendingLooseVideoDrop, setPendingLooseVideoDrop] = useState<{ filesObject: { [key: string]: File[] } } | null>(null);
  const [showLooseVideoChoiceModal, setShowLooseVideoChoiceModal] = useState(false);

  let modalText: string;
  switch (true) {
    case navigator.userAgent.includes('Firefox'):
      modalText = 'Drop package or video here';
      break;
    case isChromeBrowser:
      modalText = 'Drag & drop a package or video. Click to select MP4/MOV/M4V videos.';
      break;
    case isSafariBrowser:
      modalText = 'Click to select a package or video. Drag & drop supports MP4/MOV/M4V videos only.';
      break;

    default:
      modalText = 'Upload package or video by click';
      break;
  }

  const isSupportedVideoExtension = (name: string) => /\.(mov|mp4|m4v)$/i.test(name);
  const precheckWarningHeader =
    "If you proceed with this upload, the uploaded content may be corrupted or fail during processing and the upload hours may still contribute to your organisation's total.";

  const resolveVideoPath = (file: File) => {
    const customPath = (file as File & { customPath?: string }).customPath || '';
    const webkitPath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || '';
    const rawPath = webkitPath || customPath || file.name;
    if (rawPath.endsWith(file.name)) {
      return rawPath;
    }
    return `${rawPath}/${file.name}`;
  };

  const formatFps = (fps: number) => {
    if (!Number.isFinite(fps)) {
      return '';
    }

    const rounded = Number(fps.toFixed(3));
    return `${String(rounded).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1')} fps`;
  };

  const normalizeFps = (fps: number) => {
    if (!Number.isFinite(fps)) {
      return null;
    }

    const commonFpsValues = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60];
    for (const value of commonFpsValues) {
      if (Math.abs(fps - value) <= 0.35) {
        return value;
      }
    }

    return Number(fps.toFixed(3));
  };

  const getStableFpsValues = (fpsValues: Array<number | null | undefined>) => {
    const FPS_MIN = 10;
    const FPS_MAX = 120;
    const deduped: number[] = [];

    fpsValues.forEach((value) => {
      if (!Number.isFinite(Number(value))) {
        return;
      }

      const numericValue = Number(value);
      if (numericValue < FPS_MIN || numericValue > FPS_MAX) {
        return;
      }

      const normalized = normalizeFps(numericValue);
      if (normalized === null) {
        return;
      }

      const exists = deduped.some((existing) => Math.abs(existing - normalized) <= 0.35);
      if (!exists) {
        deduped.push(normalized);
      }
    });

    return deduped.sort((a, b) => a - b);
  };

  const isLooseVideoDrop = (filesObject: { [key: string]: File[] }) => {
    const files = Object.values(filesObject || {}).flat();
    return (
      files.length > 1 &&
      files.every((file) => isSupportedVideoExtension(file.name) && !resolveVideoPath(file).includes('/'))
    );
  };

  const isResumableLooseVideoDrop = (filesObject: { [key: string]: File[] }) => {
    const folderId = findSearchValue('folderId');
    if (!folderId) {
      return false;
    }

    const droppedVideos = Object.values(filesObject || {})
      .flat()
      .filter((file) => isSupportedVideoExtension(file.name));

    if (droppedVideos.length <= 1) {
      return false;
    }

    const droppedNames = droppedVideos
      .map((file) => file.name)
      .sort((a, b) => a.localeCompare(b))
      .join('|');
    const droppedSize = droppedVideos.reduce((total, file) => total + file.size, 0);
    const prefix = `=videofeedToResume=${folderId}::`;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) {
        continue;
      }

      const raw = localStorage.getItem(key);
      if (!raw) {
        continue;
      }

      try {
        const parsed = JSON.parse(raw);
        if ((parsed?.uploadMode || 'package') === 'append') {
          continue;
        }

        const resumableNames = (parsed?.fileListNames?.length
          ? parsed.fileListNames
          : (parsed?.videos || []).map((video: any) => video?.name || video?.angle_name || video?.video_id || '')
        )
          .filter(Boolean)
          .sort((a: string, b: string) => a.localeCompare(b))
          .join('|');
        const resumableSize = Number(parsed?.size || 0);

        if (resumableSize === droppedSize && resumableNames === droppedNames) {
          return true;
        }
      } catch {
        // ignore malformed resume payloads
      }
    }

    return false;
  };

  const openLooseVideoChoiceModal = (filesObject: { [key: string]: File[] }) => {
    setPendingLooseVideoDrop({ filesObject });
    setShowLooseVideoChoiceModal(true);
  };

  const runUploadPrecheck = async (packageName: string, movFiles: File[], uploadMode?: UploadMode) => {
    if (!movFiles.length) {
      return true;
    }

    const inspections = await Promise.all(
      movFiles.map(async (file) => {
        try {
          return {
            file,
            inspection: await inspectVideoFileForUpload(file),
          };
        } catch (error) {
          console.warn('Upload precheck skipped for file', file.name, error);
          return {
            file,
            inspection: null,
          };
        }
      }),
    );

    const blockingIssues: UploadPrecheckIssue[] = [];
    const warningIssues: UploadPrecheckIssue[] = [];
    const packageResolutions = new Set<string>();
    const packageFps = new Set<number>();

    inspections.forEach(({ file, inspection }) => {
      if (!inspection) {
        return;
      }

      inspection.resolutions.forEach((resolution) => packageResolutions.add(resolution));

      const hasHevc = inspection.codecTypes.some((codecType) => ['hvc1', 'hev1'].includes(String(codecType || '').toLowerCase()));
      const filePath = resolveVideoPath(file);
      if (hasHevc) {
        blockingIssues.push({
          targetPath: filePath,
          severity: 'blocking',
          message: 'Contains HEVC video tracks. Please transcode via Angles before uploading.',
        });
      }

      const audioTracks = inspection.audioTracks || [];
      const emptyAudioTracks = audioTracks.filter((track) => track.hasSampleBytes === false);
      const mixedAudioDescriptions = audioTracks.filter((track) => track.sampleDescriptionIndexes.length > 1);

      if (emptyAudioTracks.length) {
        blockingIssues.push({
          targetPath: filePath,
          severity: 'blocking',
          message:
            'Contains an empty audio track. Transcoding may fail with no audio frames decoded. Please transcode via Angles before uploading.',
        });
      }

      if (mixedAudioDescriptions.length) {
        blockingIssues.push({
          targetPath: filePath,
          severity: 'blocking',
          message:
            'Contains mixed audio sample descriptions in one track. Transcoding may fail with no audio frames decoded. Please transcode via Angles before uploading.',
        });
      }

      const mixedVideoDescriptions = inspection.tracks.filter((track) => track.sampleDescriptionIndexes.length > 1);
      if (mixedVideoDescriptions.length) {
        warningIssues.push({
          targetPath: filePath,
          severity: 'warning',
          message:
            'Contains multiple video format descriptions in one track. This file may cause transcoding errors or corrupted playback. We recommend transcoding it before upload.',
        });
      }

      const trackAverageFps = getStableFpsValues(inspection.tracks.map((track) => track.averageFps));
      trackAverageFps.forEach((fps) => packageFps.add(fps));

      if (trackAverageFps.length > 1) {
        blockingIssues.push({
          targetPath: filePath,
          severity: 'blocking',
          message: `Contains multiple video frame rates (${trackAverageFps
            .map(formatFps)
            .join(', ')}). Please transcode via Angles before uploading.`,
        });
      }

      if (inspection.resolutions.length > 1) {
        warningIssues.push({
          targetPath: filePath,
          severity: 'warning',
          message: `Contains multiple video resolutions (${inspection.resolutions.join(
            ', ',
          )}). We recommend transcoding before upload because playback may be corrupted after transcode.`,
        });
      }
    });

    const packageFpsList = getStableFpsValues(Array.from(packageFps));
    if (uploadMode === 'package' || !uploadMode) {
      if (movFiles.length > 1 && packageFpsList.length > 1) {
        blockingIssues.push({
          targetPath: `${packageName} (package)`,
          severity: 'blocking',
          message: `Contains video tracks with different frame rates (${packageFpsList
            .map(formatFps)
            .join(', ')}). Please transcode via Angles before uploading.`,
        });
      }

      if (movFiles.length > 1 && packageResolutions.size > 1) {
        warningIssues.push({
          targetPath: `${packageName} (package)`,
          severity: 'warning',
          message: `Contains video tracks with different resolutions (${Array.from(packageResolutions).join(
            ', ',
          )}). We recommend transcoding before upload because playback may be corrupted after transcode.`,
        });
      }
    }

    const issues = [...blockingIssues, ...warningIssues];
    if (issues.length) {
      const uniqueIssues = Array.from(
        new Map(issues.map((issue) => [`${issue.targetPath}|${issue.severity}|${issue.message}`, issue])).values(),
      );
      const grouped = uniqueIssues.reduce<Map<string, UploadPrecheckGroup>>((acc, issue) => {
        const currentGroup = acc.get(issue.targetPath) || {
          targetPath: issue.targetPath,
          issues: [],
          blockingCount: 0,
          warningCount: 0,
        };
        currentGroup.issues.push(issue);
        if (issue.severity === 'blocking') {
          currentGroup.blockingCount += 1;
        } else {
          currentGroup.warningCount += 1;
        }
        acc.set(issue.targetPath, currentGroup);
        return acc;
      }, new Map());

      const groupedArr = Array.from(grouped.values()).sort((a, b) => {
        if (a.blockingCount !== b.blockingCount) {
          return b.blockingCount - a.blockingCount;
        }
        return a.targetPath.localeCompare(b.targetPath);
      });
      setUploadPrecheckGroups(groupedArr);
      setExpandedPrecheckPaths(
        groupedArr.length
          ? {
              [groupedArr[0].targetPath]: true,
            }
          : {},
      );
      setShowUploadPrecheckModal(true);
    }

    return true;
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
    wasDropped?: boolean,
    droppedFileList?: { [key: string]: File[] },
    looseUploadMode?: UploadMode,
  ) => {
    const fileArr: { videos: File[]; zip: Blob | null }[] = [];
    let filesObject = { ...droppedFileList };

    try {
      setLoading(true);
      if (!wasDropped && event.target.files) {
        const files = event.target.files as any;
        const fileNamesObject: any = {};
        for (let i = 0; i < files.length; i++) {
          const packageName = files[i].webkitRelativePath.split('/')[0] ? files[i].webkitRelativePath.split('/')[0] : files[i].name;
          files[i].customPath = packageName;
          if (fileNamesObject[packageName]) {
            fileNamesObject[packageName] = [...fileNamesObject[packageName], files[i]];
          } else {
            fileNamesObject[packageName] = [files[i]];
          }
        }
        filesObject = { ...fileNamesObject };
      }

      if (!looseUploadMode && isLooseVideoDrop(filesObject) && !isResumableLooseVideoDrop(filesObject)) {
        openLooseVideoChoiceModal(filesObject);
        setTimeout(() => {
          setRearrangeDisabled(false);
        }, 3000);
        return;
      }

      const filesList: TLocalPackage[] = [];
      if (filesObject) {
        const mergedFilesObject: { [key: string]: any[] } = looseUploadMode === 'separate' ? { ...filesObject } : {};

        // Iterate through filesObject and merge files with supported video extensions
        for (let key in filesObject) {
          if (looseUploadMode !== 'separate' && isSupportedVideoExtension(key)) {
            const mergedKey = Object.keys(mergedFilesObject).find(
              (k) => isSupportedVideoExtension(k),
            );
            const targetKey = mergedKey || key;

            if (!mergedFilesObject[targetKey]) {
              mergedFilesObject[targetKey] = [];
            }

            mergedFilesObject[targetKey] = mergedFilesObject[targetKey].concat(filesObject[key]);
          } else {
            // Preserve files with other extensions in their original keys
            mergedFilesObject[key] = filesObject[key];
          }
        }

        const fileListNames: string[] = [];
        for (let key in mergedFilesObject) {
          const movFiles: File[] = [];
          const dataFiles: File[] = [];
          let singleMov = false;

          if (isSupportedVideoExtension(key)) {
            singleMov = true;
            if (mergedFilesObject[key].length > 1) {
              mergedFilesObject[key].forEach((f) => fileListNames.push(f.name));
            }
          }

          let plistPaths: any = [];
          let plistForZip: any = [];
          let filledMovieFolders: any = [];
          let corePlistFiles = 0;
          for (let i = 0; i < mergedFilesObject[key].length; i++) {
            if (isSupportedVideoExtension(mergedFilesObject[key][i].name)) {
              if (mergedFilesObject[key][i].webkitRelativePath.includes('/Single Videos/')) {
                plistPaths.push({
                  path: '/Single Videos/',
                });
                plistForZip.push({
                  path: '/Single Videos/',
                });
              } else if (mergedFilesObject[key][i].webkitRelativePath.includes('/Single Videos Local/')) {
                plistPaths.push({
                  path: '/Single Videos Local/',
                });
                plistForZip.push({
                  path: '/Single Videos Local/',
                });
              }

              if (isSafari()) {
                const str = mergedFilesObject[key][i].webkitRelativePath;
                const folderPath = str.substring(0, str.lastIndexOf('/'));
                filledMovieFolders.push(folderPath);
              } else {
                const str = mergedFilesObject[key][i].customPath;
                const folderPath = str.substring(0, str.lastIndexOf('/'));
                filledMovieFolders.push(folderPath);
              }

              movFiles.push(mergedFilesObject[key][i]);
              const { height: movResolution } = await getVideoResolution(mergedFilesObject[key][i]);
              if (movResolution < 480) {
                notification.showMessage('Please ensure that all packages contain videos with a minimum resolution of 480p.');
                return;
              }

              if (movResolution > 1080) {
                notification.showMessage('Video resolution higher than 1080p will be downgraded to 1080p, taking more time to transcode.');
              }
            } else {
              if (
                mergedFilesObject[key][i].name.endsWith('composer data.plist') ||
                mergedFilesObject[key][i].name.endsWith('chron data.plist')
              ) {
                corePlistFiles++;
              }

              dataFiles.push(mergedFilesObject[key][i]);
            }
          }

          if (looseUploadMode === 'package' || !looseUploadMode) {
            const uniqueFolders = [...new Set(filledMovieFolders)];
            const hasDuplicates = uniqueFolders.length !== filledMovieFolders.length;
            if (uniqueFolders.length === 1 && uniqueFolders[0] === '' && filledMovieFolders?.length > 4) {
              notification.showMessage(NOTIFICATION.CLIPS_COUNT_SHOULD_BE_LESS_OR_EQUAL_4);
              return;
            }

            const groups = new Set<string>();
            filledMovieFolders.forEach((p: string) => {
              const parts = p.split('/');
              const i = parts.findIndex((v, j) => v === 'Panels' && parts[j + 1] === 'Video Groups');
              if (i > -1) groups.add(parts[i + 2]);
            });
            const hasMultipleVideoGroups = groups.size > 1;

            if (corePlistFiles > 1) {
              notification.showMessage(NOTIFICATION.AMBIGUOUS_PACKAGE);
              return;
            } else if (hasMultipleVideoGroups) {
              notification.showMessage(NOTIFICATION.MULTIPLE_VIDEO_GROUPS);
              return;
            } else if (uniqueFolders.length === 0 || uniqueFolders.length > 4) {
              notification.showMessage(NOTIFICATION.CLIPS_COUNT_SHOULD_BE_LESS_OR_EQUAL_4);
              return;
            } else if (hasDuplicates && dataFiles?.length > 0) {
              notification.showMessage(NOTIFICATION.TOO_MANY_MOVIE_PARTS);
              return;
            }
          }

          const shouldContinueUpload = await runUploadPrecheck(
            singleMov ? movFiles.map((file) => file.name).join(', ') || key : transformedNameFromKey(key),
            movFiles,
            looseUploadMode,
          );
          if (!shouldContinueUpload) {
            return;
          }

          // if (movFiles.length > 4 || movFiles.length === 0) {
          //   break;
          // } else if (filledMovieFolders.length !== movFiles.length) {
          //   break;
          // }

          const zip = new JSZip();
          // add files to zip
          for (let i = 0; i < dataFiles.length; i++) {
            const file = dataFiles[i];
            const fileContent = await file.arrayBuffer(); // Read file content as ArrayBuffer

            const filePath = wasDropped
              ? // @ts-ignore
                file.customPath
              : file.webkitRelativePath; // Get the relative path of the file
            let folders = filePath.split('/').slice(0, -1); // Extract the folder structure
            let currentFolder = zip; // Start with the root folder of the zip
            if (filePath.includes('/Single Videos/')) {
              if (plistPaths[0]?.path && plistPaths[0]?.path === '/Single Videos/') {
                plistPaths.shift();
              } else if (plistPaths[0]?.path && plistPaths[0]?.path !== '/Single Videos/') {
                continue;
              }

              if (file.name.endsWith('.plist')) {
                let movExist = false;
                let path = '';
                if (file.webkitRelativePath) {
                  path = file.webkitRelativePath.split('/').at(-2) as string;

                  // @ts-ignore
                } else if (file.customPath) {
                  // @ts-ignore
                  path = file.customPath.split('/').at(-2) as string;
                }
                for (let movIndex = 0; movIndex < movFiles.length; movIndex++) {
                  const { height: movResolution, duration } = await getVideoResolution(movFiles[movIndex]);
                  if (movResolution < 480) {
                    notification.showMessage('Please ensure that all packages contain videos with a minimum resolution of 480p.');
                    return;
                  }
                  const movFilePath = movFiles[movIndex].webkitRelativePath
                    ? movFiles[movIndex].webkitRelativePath
                    : // @ts-ignore
                      movFiles[movIndex].customPath;
                  if (movFilePath.includes(path)) {
                    movExist = true;
                  }
                }
                if (!movExist) {
                  folders = '';
                  // eslint-disable-next-line no-continue
                  continue;
                }
              }
            }

            if (filePath.includes('/Single Videos Local/')) {
              if (plistPaths[0]?.path && plistPaths[0]?.path === '/Single Videos Local/') {
                plistPaths.shift();
              } else if (plistPaths[0]?.path && plistPaths[0]?.path !== '/Single Videos Local/') {
                continue;
              }

              if (file.name.endsWith('.plist')) {
                let movExist = false;
                let path = '';
                if (file.webkitRelativePath) {
                  path = file.webkitRelativePath.split('/').at(-2) as string;

                  if (file.webkitRelativePath.includes('Single Videos Local')) {
                    const match = file.webkitRelativePath.match(/Single Videos Local\/Video [^\/]+/);
                    if (match?.length) {
                      const id = match[0].split('/')[1];
                      if (id) {
                        path = id;
                      }
                    }
                  }

                  // @ts-ignore
                } else if (file.customPath) {
                  // @ts-ignore
                  path = file.customPath.split('/').at(-2) as string;

                  // @ts-ignore
                  if (file.customPath.includes('Single Videos Local')) {
                    // @ts-ignore
                    const match = file.customPath.match(/Single Videos Local\/Video [^\/]+/);
                    if (match?.length) {
                      const id = match[0].split('/')[1];
                      if (id) {
                        path = id;
                      }
                    }
                  }
                }
                for (let movIndex = 0; movIndex < movFiles.length; movIndex++) {
                  const { height: movResolution, duration } = await getVideoResolution(movFiles[movIndex]);
                  if (movResolution < 480) {
                    notification.showMessage('Please ensure that all packages contain videos with a minimum resolution of 480p.');
                    return;
                  }
                  const movFilePath = movFiles[movIndex].webkitRelativePath
                    ? movFiles[movIndex].webkitRelativePath
                    : // @ts-ignore
                      movFiles[movIndex].customPath;

                  if (movFilePath.includes(path)) {
                    movExist = true;
                  }
                }
                if (!movExist) {
                  folders = '';
                  // eslint-disable-next-line no-continue
                  continue;
                }
              }
            }

            // Create nested folders in the zip
            for (const folder of folders) {
              // @ts-ignore
              currentFolder = currentFolder.folder(folder);
            }

            // Add the file to the appropriate folder
            currentFolder.file(file.name, fileContent);
          }

          let transformedName = key;
          if (/\.(mov|mp4|m4v|avault|vault)$/i.test(key)) {
            transformedName = key.replace(/\.(mov|mp4|m4v|avault|vault)$/i, '');
          }

          // Generate the zip file
          await zip
            .generateAsync({ type: 'blob' })
            .then((zipContent) => {
              // Handle the zip file content
              fileArr.push({ videos: movFiles, zip: zipContent });
              filesList.push({
                name: singleMov ? (movFiles.length > 1 ? '' : transformedName) : transformedName,
                size: movFiles.reduce((a, b) => a + b.size, 0) + dataFiles.reduce((a, b) => a + b.size, 0),
                progress: 0,
                zipContent,
                movFiles,
                upload: false,
                singleMov,
                originalName: transformedName,
                multi_quality: false,
                fileListNames,
                uploadMode: looseUploadMode,
              });
            })
            .catch((error) => {
              notification.showMessage('Please try again, or reload the page');
              console.error('Error generating zip file:', error);
            });
        }
        filesList.sort((f, b) => f.size - b.size);
        setPackageList(filesList);
        return;
      }
    } finally {
      setLoading(false);
      setInputKey((prevKey) => prevKey + 1);
    }
  };

  const openUploader = () => {
    if (disable) {
      return;
    }

    inputRef?.current?.click();
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (disable) {
      return;
    }

    if (isSafariBrowser) {
      const droppedFiles = Array.from(event.dataTransfer.files || []);
      const hasDirectory = Array.from(event.dataTransfer.items || []).some((item) => {
        const entry = (item as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry?.();
        return Boolean(entry?.isDirectory);
      });

      if (hasDirectory || !droppedFiles.length || droppedFiles.some((file) => !isSupportedVideoExtension(file.name))) {
        notification.showMessage('Safari drag & drop supports MP4/MOV/M4V videos. Select package folders or aVault files by clicking.');
        return;
      }

      const filesObject = droppedFiles.reduce<{ [key: string]: File[] }>((acc, file) => {
        (file as File & { customPath?: string }).customPath = file.name;
        acc[file.name] = [...(acc[file.name] || []), file];
        return acc;
      }, {});

      await handleFileSelect(
        ({ target: { files: droppedFiles } } as unknown) as React.ChangeEvent<HTMLInputElement>,
        true,
        filesObject,
      );
      setTimeout(() => {
        setRearrangeDisabled(false);
      }, 3000);
      return;
    }

    try {
      const dt = event.dataTransfer;
      if (dt && dt.items) {
        const items = dt.items;
        const droppedFiles: File[] = [];
        const filesObject: any = {};

        // @ts-ignore
        const traverseFileTree = (item, path = '') => {
          return new Promise<void>((resolve) => {
            if (item.isFile) {
              item.file((file: File) => {
                // @ts-ignore
                file.customPath = item.fullPath.slice(1, item.fullPath.length);
                // @ts-ignore
                const folderName = file.customPath.split('/')[0];
                if (filesObject[folderName]) {
                  filesObject[folderName] = [...filesObject[folderName], file];
                } else {
                  filesObject[folderName] = [file];
                }
                droppedFiles.push(file);
                resolve();
              });
            } else if (item.isDirectory) {
              const directoryReader = item.createReader();

              // @ts-ignore
              directoryReader.readEntries(async (entries) => {
                // @ts-ignore
                const entriesPromises = entries.map((entry) => traverseFileTree(entry, `${path}${item.name}/`));
                await Promise.all(entriesPromises);
                resolve();
              });
            } else {
              resolve();
            }
          });
        };

        const entryPromises = Array.from(items).map((item) => {
          return traverseFileTree(
            // @ts-ignore
            item.webkitGetAsEntry() || item.mozGetAsEntry(),
          );
        });
        await Promise.all(entryPromises);
        handleFileSelect(
          {
            target: {
              // @ts-ignore
              files: droppedFiles,
            },
          },
          true,
          filesObject,
        );
        setTimeout(() => {
          setRearrangeDisabled(false);
        }, 3000);
      } else {
        notification.showMessage('Error for accessing folder contents. (Browser limitations)');
      }
    } catch (err) {
      console.log(err);
    }
  };

  const [inputKey, setInputKey] = useState(0);

  function transformedNameFromKey(key: string) {
    if (/\.(mov|mp4|m4v|avault|vault)$/i.test(key)) {
      return key.replace(/\.(mov|mp4|m4v|avault|vault)$/i, '');
    }

    return key;
  }

  const closeLooseVideoChoiceModal = () => {
    setShowLooseVideoChoiceModal(false);
    setPendingLooseVideoDrop(null);
  };

  const continueLooseVideoDrop = async (uploadMode: UploadMode) => {
    if (!pendingLooseVideoDrop) {
      closeLooseVideoChoiceModal();
      return;
    }

    const { filesObject } = pendingLooseVideoDrop;
    closeLooseVideoChoiceModal();
    await handleFileSelect(
      ({ target: { files: [] } } as unknown) as React.ChangeEvent<HTMLInputElement>,
      true,
      filesObject,
      uploadMode,
    );
  };

  return (
    <div onClick={openUploader} onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDrop={handleDrop} className={styles.fileUpload}>
      <div className={disable ? styles.disabled : styles.enabled}>
        <input
          type="file"
          multiple
          {...filePickerProps}
          onChange={handleFileSelect}
          ref={inputRef}
          hidden
          key={inputKey}
        />

        {small ? (
          <>
            <div className={styles.text}>Upload More</div>
            <div className={styles.warningText}>Only 4-angles packages are allowed</div>
          </>
        ) : (
          <>
            <img src={UploadFileIcon} style={{ marginBottom: '12px' }} alt="upload content" />
            <div className={styles.text}>{modalText}</div>
            <div className={styles.warningText}>Only 4-angles packages are allowed</div>
          </>
        )}
      </div>
      {loading && <LinearProgress />}
      <Modal open={showLooseVideoChoiceModal} onClose={closeLooseVideoChoiceModal}>
        <div className={styles.uploadPrecheckModalBody} onClick={(e) => e.stopPropagation()}>
          <div className={styles.uploadPrecheckModalHeader}>Multiple videos detected</div>
          <div className={styles.uploadPrecheckIntro}>
            Choose how to handle loose videos. Multi-angle collection is only available for up to 4 videos.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
            {pendingLooseVideoDrop &&
              Object.values(pendingLooseVideoDrop.filesObject).reduce((count, files) => count + files.length, 0) <= 4 && (
              <ContainedButton kind="purple" onClick={() => continueLooseVideoDrop('package')}>
                Create Multi-Angle Collection
              </ContainedButton>
            )}
            <ContainedButton kind="purple" onClick={() => continueLooseVideoDrop('append')}>
              Append videos
            </ContainedButton>
            <OutlinedButton onClick={() => continueLooseVideoDrop('separate')}>Queue Videos separately</OutlinedButton>
            <OutlinedButton onClick={closeLooseVideoChoiceModal}>Cancel</OutlinedButton>
          </div>
        </div>
      </Modal>
      <Modal
        open={showUploadPrecheckModal}
        onClose={() => {
          setShowUploadPrecheckModal(false);
          setExpandedPrecheckPaths({});
        }}
      >
        <div className={styles.uploadPrecheckModalBody} onClick={(e) => e.stopPropagation()}>
          <div className={styles.uploadPrecheckModalHeader}>Upload Precheck Warning</div>
          <div className={styles.uploadPrecheckIntro}>{precheckWarningHeader}</div>
          <div className={styles.uploadPrecheckGroups}>
            {uploadPrecheckGroups.map((group) => (
              <div key={group.targetPath} className={styles.uploadPrecheckGroup}>
                <div className={styles.uploadPrecheckGroupHeader}>
                  <div className={styles.uploadPrecheckGroupPath}>{group.targetPath}</div>
                  <div className={styles.uploadPrecheckGroupMeta}>
                    {group.blockingCount > 0 && (
                      <span className={styles.uploadPrecheckBlockingBadge}>{group.blockingCount} critical</span>
                    )}
                    {group.warningCount > 0 && (
                      <span className={styles.uploadPrecheckWarningBadge}>{group.warningCount} warning</span>
                    )}
                    <button
                      type="button"
                      className={styles.uploadPrecheckToggle}
                      onClick={() =>
                        setExpandedPrecheckPaths((prev) => ({
                          ...prev,
                          [group.targetPath]: !prev[group.targetPath],
                        }))
                      }
                    >
                      {expandedPrecheckPaths[group.targetPath] ? 'Hide details' : 'Show details'}
                    </button>
                  </div>
                </div>
                {expandedPrecheckPaths[group.targetPath] && (
                  <div className={styles.uploadPrecheckIssues}>
                    {group.issues.map((issue, index) => (
                      <div key={`${group.targetPath}-${issue.message}-${index}`} className={styles.uploadPrecheckIssue}>
                        {issue.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className={styles.uploadPrecheckActions}>
            <ContainedButton kind="purple" onClick={() => setShowUploadPrecheckModal(false)}>
              OK
            </ContainedButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FileUpload;
